import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const UserProfile = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const API_BASE = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    name: '',
    clinicId: '',
    logourl: '',
    address: '',
    gst: '',
    mobile: '',
    phone: '',
    email: '',
    pinCode: ''
  });
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');

  const buildLogoUrl = (value) => {
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    const baseUrl = API_BASE || process.env.REACT_APP_API_URL || '';
    const sep = baseUrl && baseUrl.endsWith('/') ? '' : '/';
    const fileParam = encodeURIComponent(value);
    return `${baseUrl}${sep}patient-medical-history/file-url?fileName=${fileParam}`;
  };

  const loadLogoPreviewWithAuth = async (fileName) => {
    try {
      const token = localStorage.getItem('auth_token');
      const tenantId = localStorage.getItem('tenantId');
      if (!fileName || !token || !tenantId) return;
      const url = buildLogoUrl(fileName);
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          tenantId: tenantId,
          Accept: '*/*'
        },
        redirect: 'follow'
      });
      if (!res.ok) return;
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        try {
          const data = await res.json();
          if (data && typeof data.signedUrl === 'string' && /^https?:\/\//i.test(data.signedUrl)) {
            setLogoPreviewUrl(data.signedUrl);
            return;
          }
        } catch (_) {}
      } else if (contentType.includes('text/')) {
        const text = await res.text();
        if (/^https?:\/\//i.test(text)) {
          setLogoPreviewUrl(text);
          return;
        }
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      setLogoPreviewUrl(objectUrl);
    } catch (e) {
      console.error('Failed to load logo preview:', e);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isFormValid = () => {
    return (
      formData.name.trim() &&
      formData.email.trim() &&
      formData.mobile.trim().length === 10 &&
      formData.pinCode.trim().length === 6
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");
      const stored = localStorage.getItem("categoryType");
      const isHospi360 = stored === "HOSPI360";

      try {
        const url = isHospi360 ? `${API_BASE}hospital` : `${API_BASE}clinicDetails`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}`, tenantId: tenantId },
        });

        const data = response.data || {};
        const logoPath =
          data.hospitalLogoUrl ||
          data.hospitalLogo ||
          data.logoUrl ||
          data.logourl ||
          "";

        setFormData({
          name: data.name || data.hospitalName || data.firstName || "",
          clinicId:
            data.clinicId || data.hospitalId || data.id || data.userId || "",
          logourl: logoPath,
          address: data.address || "",
          gst: data.gst || "",
          mobile: data.mobileNumber || data.mobile || data.phoneNumber || "",
          phone: data.telephoneNumber || "",
          email: data.email || "",
          pinCode: data.pinCode || "",
        });

        if (logoPath) {
          loadLogoPreviewWithAuth(logoPath);
        } else {
          setLogoPreviewUrl("");
        }
      } catch (err) {
        console.error(
          `Error fetching ${isHospi360 ? "hospital" : "clinic"} details:`,
          err
        );
      }
    };

    fetchOrganizationDetails();
  }, [API_BASE]);

  return (
    <div className="max-w-full rounded-lg mx-auto my-5 p-5 font-sans h-screen bg-[#f1f8f7]">
      <div className="mb-8 pb-4 border-b border-gray-200 flex items-center">
        {logoPreviewUrl && (
          <img 
            src={logoPreviewUrl} 
            alt="Clinic Logo" 
            className="w-16 h-16 rounded-full object-cover mr-4"
          />
        )}
        
        <div>
          <h1 className="m-0 text-gray-800 text-2xl font-semibold">{formData.name}</h1>
          {/* <p className="mt-1 text-gray-500 text-sm">Organization ID: {formData.clinicId}</p> */}
        </div>
      </div>

      <div className=" bg-[#f1f8f7] p-6 rounded-lg shadow-md">
        <h2 className="mt-0 text-[#004276] border-b border-gray-200 pb-3 text-xl font-semibold">Organization Information</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="flex gap-5 mb-4">
            <div className="flex-1 min-w-0">
              <label className="block mb-1 font-semibold text-[#004276] text-sm">Organization Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
                required
                readOnly
              />
            </div>

            <div className="flex-1 min-w-0">
              <label className="block mb-1 font-semibold text-[#004276] text-sm">GST Number</label>
              <input
                type="text"
                name="gst"
                value={formData.gst}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
                readOnly
              />
            </div>
          </div>

          <div className="flex gap-5 mb-4">
            <div className="flex-1 min-w-0">
              <label className="block mb-1 font-semibold text-[#004276] text-sm">Email ID *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
                required
                readOnly
              />
            </div>

            <div className="flex-1 min-w-0">
              <label className="block mb-1 font-semibold text-[#004276] text-sm">Mobile number *</label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
                maxLength={10}
                required
                readOnly
              />
            </div>
          </div>

          <div className="flex gap-5 mb-4">
            <div className="flex-1 min-w-0">
              <label className="block mb-1 font-semibold text-[#004276] text-sm">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
                readOnly
              />
            </div>

            <div className="flex-1 min-w-0">
              <label className="block mb-1 font-semibold text-[#004276] text-sm">Pin Code *</label>
              <input
                type="text"
                name="pinCode"
                value={formData.pinCode}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
                maxLength={6}
                required
                readOnly
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block mb-1 font-semibold text-[#004276] text-sm">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
              rows={3}
              readOnly
            />
          </div>

          {/* <div className="flex justify-end mt-8 gap-4">
            <button
              type="button"
              className="px-5 py-2 border border-gray-300 rounded cursor-pointer bg-white text-gray-600 font-semibold text-sm"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-5 py-2 border-none rounded cursor-pointer bg-[#004276] text-white font-semibold text-sm transition-colors ${
                (!isFormValid() || isSubmitting) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#00315a]'
              }`}
              disabled={!isFormValid() || isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div> */}
        </form>
      </div>
    </div>
  );
};

export default UserProfile;